import React from 'react'
import { useTheme, useMediaQuery, DialogTitle, IconButton, Typography, DialogContent, Box } from '@mui/material'
import MuiDialog from '@mui/material/Dialog'
import CloseIcon from '@mui/icons-material/Close'
import { makeStyles } from 'tss-react/mui'
import Actions from './Actions'
import { UpToThreeActions } from './types'
import { Transition } from '../Transition'
import { J1LeftarrowC, J2Setting } from '@e3dc-react/icons'

interface StyleProps {
  place?: number
  app?: string
  scrollable: boolean
  fullScreen: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  editButton: {
    position: 'absolute',
    right: theme.spacing(6),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  /* TODO: should be removed if we make all mobile dialogs full screen. Currently it is maxed by 90% width */
  paperFullWidth: {
    width: '100% !important',
    height: '100% !important',
    maxHeight: '100% !important',
    padding: 0,
    margin: 0,
    borderRadius: 0,
  },
  rootFullWidth: {
    padding: '0 !important',
    margin: '0 !important',
  },
  dialog: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 5,
      paddingTop: (props?.place ?? 0) * 15 + 5,
    },
    '& .MuiDialog-paper.MuiPaper-root': {
      paddingBottom: 0,
      overflowX: props.scrollable ? undefined : 'hidden',
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  dialogTitle: {
    minHeight: theme.shape.dialog.title.minHeight,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 0,
    [`${theme.breakpoints.down('md')} and (hover: none)`]: {
      marginTop: 0,
      paddingTop: 0,
    },
  },
  dialogDivider: {
    borderTop: '2px solid',
    borderColor: theme.palette.grey.grey06,
    margin: `0 ${theme.shape.dialogDivider}px`,
  },
  title: {
    position: 'relative',
    marginLeft: theme.spacing(2),
    color: theme.palette.text.paperTitle,
  },
  navigateBackButton: {
    paddingLeft: theme.spacing(4),
  },
}))

interface DialogProps {
  title: string
  onClose: () => void
  onNavigateBack?: () => void
  onEdit?: () => void
  place?: number
  open: boolean
  actions?: UpToThreeActions
  navigateBackButton?: boolean
  closeButtonVisible?: boolean
  editButtonVisible?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  scrollable?: boolean
  className?: string
  titleClassName?: string
  children?: React.ReactNode
  fullScreen?: boolean
  dialogTitleClassName?: string
  closeButtonClassName?: string
  editButtonClassName?: string
}

/**
 * Dialog with simple title, content and 1-3 actions.
 */
const ActionDialog: React.FC<DialogProps> = ({
  open,
  title,
  onClose,
  onNavigateBack,
  onEdit,
  place,
  actions,
  children,
  className,
  titleClassName,
  dialogTitleClassName,
  closeButtonClassName,
  editButtonClassName,
  maxWidth = 'md',
  scrollable = true,
  fullScreen = false,
  navigateBackButton = false,
  closeButtonVisible = true,
  editButtonVisible = false,
}) => {
  const theme = useTheme()
  const useFullScreen = useMediaQuery(`${theme.breakpoints.down('md')} and (hover: none)`) || fullScreen // aims at phones and tablets
  const { classes, cx } = useStyles({ place, scrollable, fullScreen })

  return (
    <MuiDialog
      open={open}
      fullScreen={useFullScreen}
      maxWidth={maxWidth}
      fullWidth
      className={cx(classes.dialog, className)}
      TransitionComponent={Transition}
      keepMounted
      TransitionProps={{ onExited: () => onClose() }}
      onClose={onClose}
      classes={
        fullScreen
          ? {
              paper: classes.paperFullWidth,
              root: classes.rootFullWidth,
            }
          : {}
      }
    >
      <DialogTitle className={cx(classes.dialogTitle, dialogTitleClassName)} component={'div'}>
        {navigateBackButton ? (
          <IconButton data-qa={'button-modal-close-alternative'} onClick={onNavigateBack} className={classes.navigateBackButton}>
            <J1LeftarrowC />
          </IconButton>
        ) : null}

        <Typography variant="h3" className={cx(classes.title, titleClassName)} color="textPrimary">
          {title}
        </Typography>

        {editButtonVisible && (
          <IconButton className={cx(classes.editButton, editButtonClassName)} data-qa={'button-modal-edit'} onClick={onEdit}>
            <J2Setting />
          </IconButton>
        )}

        {closeButtonVisible ? (
          <IconButton className={cx(classes.closeButton, closeButtonClassName)} data-qa={'button-modal-close'} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>

      <Box className={classes.dialogDivider} />
      {actions && <Actions actions={actions} />}
    </MuiDialog>
  )
}

export default ActionDialog
